import { createReducer, on } from '@ngrx/store';

import { first, isNil, isNull, isUndefined, omit } from 'lodash-es';
import { DateTime } from 'luxon';

import { availableSouthernCaliforniaCounties } from '../../constants/net-sheet';
import {
  NetSheetPartyTypeEnum,
  NetSheetSplitOptionsEnum,
  NetSheetStateEnum,
  NetSheetTermEnum
} from '../../enums/net-sheet';
import { findOption } from '../../helpers/net-sheet';
import {
  NetSheetFormMiscItem,
  NetSheetNameValuePair,
  NetSheetResult,
  NetSheetState,
  NetSheetTaxesHomeownersDuesTerm,
  NetSheetTaxesTerm
} from '../../typings/net-sheet';
import * as fromNetSheetActions from '../actions/net-sheet.actions';

export const netSheetFeatureKey = 'netSheet';

export interface State {
  calculatorResult: NetSheetResult | null;
  customerInformationPartyFirstName: string | null;
  customerInformationPartyFirstNameRequired: boolean;
  customerInformationPartyFirstNameVisible: boolean;
  customerInformationPartyFullName: string | null;
  customerInformationPartyFullNameRequired: boolean;
  customerInformationPartyFullNameVisible: boolean;
  customerInformationPartyLastName: string | null;
  customerInformationPartyLastNameRequired: boolean;
  customerInformationPartyLastNameVisible: boolean;
  customerInformationPartyType: NetSheetPartyTypeEnum;
  customerInformationPartyTypes: NetSheetPartyTypeEnum[];
  miscItems: NetSheetFormMiscItem[];
  mortgagePrimaryBalance: string | null;
  mortgagePrimaryBalanceVisible: boolean;
  mortgagePrimaryInterestRate: string | null;
  mortgagePrimaryInterestRateVisible: boolean;
  mortgagePrimaryLienCloseDate: string | null;
  mortgagePrimaryLienCloseDateVisible: boolean;
  mortgagePrimaryPrepayPenalty: string | null;
  mortgagePrimaryPrepayPenaltyVisible: boolean;
  mortgageSecondaryBalance: string | null;
  mortgageSecondaryBalanceVisible: boolean;
  mortgageSecondaryInterestRate: string | null;
  mortgageSecondaryInterestRateVisible: boolean;
  mortgageSecondaryPrepayPenalty: string | null;
  mortgageSecondaryPrepayPenaltyVisible: boolean;
  salesCommissionBrokerCommissionListing: string | null;
  salesCommissionBrokerCommissionListingRequired: boolean;
  salesCommissionBrokerCommissionListingVisible: boolean;
  salesCommissionBrokerCommissionSelling: string | null;
  salesCommissionBrokerCommissionSellingRequired: boolean;
  salesCommissionBrokerCommissionSellingVisible: boolean;
  salesCommissionBrokerCommissionSplits: boolean;
  salesCommissionBrokerCommissionSplitsVisible: boolean;
  salesCommissionBrokerCommissionTaxListing: boolean;
  salesCommissionBrokerCommissionTaxListingVisible: boolean;
  salesCommissionBrokerCommissionTaxSelling: boolean;
  salesCommissionBrokerCommissionTaxSellingVisible: boolean;
  salesCommissionBrokerCommissionTaxTotal: boolean;
  salesCommissionBrokerCommissionTaxTotalVisible: boolean;
  salesCommissionBrokerCommissionTierRate: string | null;
  salesCommissionBrokerCommissionTierRateVisible: boolean;
  salesCommissionBrokerCommissionTotal: string | null;
  salesCommissionBrokerCommissionTotalRequired: boolean;
  salesCommissionBrokerCommissionTotalVisible: boolean;
  taxes: string | null;
  taxesHomeownersDuesAmount: string | null;
  taxesHomeownersDuesAmountVisible: boolean;
  taxesHomeownersDuesDueDate: string | null;
  taxesHomeownersDuesDueDateVisible: boolean;
  taxesHomeownersDuesPaid: boolean;
  taxesHomeownersDuesPaidVisible: boolean;
  taxesHomeownersDuesTerm: NetSheetTaxesHomeownersDuesTerm | null;
  taxesHomeownersDuesTermVisible: boolean;
  taxesNextInstallment: string | null;
  taxesNextInstallmentVisible: boolean;
  taxesPaid: boolean;
  taxesPaidToDateDropdown: string | null;
  taxesPaidToDateDropdownVisible: boolean;
  taxesPaidToDatePicker: string | null;
  taxesPaidToDatePickerVisible: boolean;
  taxesPaidVisible: boolean;
  taxesTerm: NetSheetTaxesTerm | null;
  taxesTermVisible: boolean;
  taxesVisible: boolean;
  transactionInformationBrokerageFee: string | null;
  transactionInformationBrokerageFeeVisible: boolean;
  transactionInformationBuyerCreditAmount: string | null;
  transactionInformationBuyerCreditAmountVisible: boolean;
  transactionInformationCityTransferTaxSplit: NetSheetSplitOptionsEnum;
  transactionInformationCityTransferTaxSplitVisible: boolean;
  transactionInformationClosingDate: string | null;
  transactionInformationClosingDateRequired: boolean;
  transactionInformationClosingDateVisible: boolean;
  transactionInformationCountyTransferTaxSplit: NetSheetSplitOptionsEnum;
  transactionInformationCountyTransferTaxSplitVisible: boolean;
  transactionInformationEscrowBranch: string | null;
  transactionInformationEscrowBranches: NetSheetNameValuePair[];
  transactionInformationEscrowBranchRequired: boolean;
  transactionInformationEscrowBranchVisible: boolean;
  transactionInformationEscrowCities: NetSheetNameValuePair[];
  transactionInformationEscrowCity: string | null;
  transactionInformationEscrowCityVisible: boolean;
  transactionInformationEscrowCounties: NetSheetNameValuePair[];
  transactionInformationEscrowCounty: string | null;
  transactionInformationEscrowCountyRequired: boolean;
  transactionInformationEscrowCountyVisible: boolean;
  transactionInformationEscrowFeeSplit: NetSheetSplitOptionsEnum;
  transactionInformationEscrowFeeSplitVisible: boolean;
  transactionInformationEscrowState: NetSheetStateEnum | null;
  transactionInformationEscrowStateRequired: boolean;
  transactionInformationEscrowStates: NetSheetState[];
  transactionInformationEscrowStateVisible: boolean;
  transactionInformationFirpta: boolean;
  transactionInformationFirptaVisible: boolean;
  transactionInformationFtb: boolean;
  transactionInformationFtbVisible: boolean;
  transactionInformationHarpta: boolean;
  transactionInformationHarptaVisible: boolean;
  transactionInformationHazardInsuranceAmount: string | null;
  transactionInformationHazardInsuranceAmountVisible: boolean;
  transactionInformationLoanPrimaryAmount: string | null;
  transactionInformationLoanPrimaryAmountRequired: boolean;
  transactionInformationLoanPrimaryAmountVisible: boolean;
  transactionInformationLoanPrimaryInterestRate: string | null;
  transactionInformationLoanPrimaryInterestRateVisible: boolean;
  transactionInformationLoanPrimaryOriginationFees: string | null;
  transactionInformationLoanPrimaryOriginationFeesVisible: boolean;
  transactionInformationMortgagePayoffCount: number | null;
  transactionInformationMortgagePayoffCountVisible: boolean;
  transactionInformationNaturalHazardDisclosure: string | null;
  transactionInformationNaturalHazardDisclosureRequired: boolean;
  transactionInformationNaturalHazardDisclosureVisible: boolean;
  transactionInformationNewLoan: boolean;
  transactionInformationNewLoanVisible: boolean;
  transactionInformationNotaryFee: string | null;
  transactionInformationNotaryFeeVisible: boolean;
  transactionInformationOwnersExemption: boolean;
  transactionInformationOwnersExemptionVisible: boolean;
  transactionInformationOwnersPolicySplit: NetSheetSplitOptionsEnum;
  transactionInformationOwnersPolicySplitVisible: boolean;
  transactionInformationPersonalResidence: boolean;
  transactionInformationPersonalResidenceVisible: boolean;
  transactionInformationPropertyAddress: string | null;
  transactionInformationPropertyAddressRequired: boolean;
  transactionInformationPropertyAddressVisible: boolean;
  transactionInformationPropertyCities: NetSheetNameValuePair[];
  transactionInformationPropertyCity: string | null;
  transactionInformationPropertyCityRequired: boolean;
  transactionInformationPropertyCityVisible: boolean;
  transactionInformationPropertyCounties: NetSheetNameValuePair[];
  transactionInformationPropertyCounty: string | null;
  transactionInformationPropertyCountyRequired: boolean;
  transactionInformationPropertyIncorporatedArea: boolean;
  transactionInformationPropertyIncorporatedAreaVisible: boolean;
  transactionInformationPropertyState: NetSheetStateEnum | null;
  transactionInformationPropertyStateRequired: boolean;
  transactionInformationPropertyStates: NetSheetState[];
  transactionInformationPropertyZip: string | null;
  transactionInformationPropertyZips: NetSheetNameValuePair[];
  transactionInformationPropertyZipVisible: boolean;
  transactionInformationReo: boolean;
  transactionInformationReoVisible: boolean;
  transactionInformationSalesPrice: string | null;
  transactionInformationSalesPriceRequired: boolean;
  transactionInformationSalesPriceVisible: boolean;
  transactionInformationSewerBill: string | null;
  transactionInformationSewerBillVisible: boolean;
  transactionInformationShortSale: boolean;
  transactionInformationShortSaleVisible: boolean;
  transactionInformationSubEscrow: boolean;
  transactionInformationSubEscrowVisible: boolean;
  transactionInformationSurveyAmendmentSplit: boolean;
  transactionInformationSurveyAmendmentSplitVisible: boolean;
  transactionInformationWarrantyPremiumAmount: string | null;
  transactionInformationWarrantyPremiumAmountVisible: boolean;
  transactionInformationWaterBill: string | null;
  transactionInformationWaterBillVisible: boolean;
  userInformationCompany: string | null;
  userInformationEmail: string | null;
  userInformationFirstName: string | null;
  userInformationLastName: string | null;
  userInformationPhone: string | null;
}

export const initialState: State = {
  calculatorResult: null,
  customerInformationPartyFirstName: null,
  customerInformationPartyFirstNameRequired: false,
  customerInformationPartyFirstNameVisible: false,
  customerInformationPartyFullName: null,
  customerInformationPartyFullNameRequired: true,
  customerInformationPartyFullNameVisible: true,
  customerInformationPartyLastName: null,
  customerInformationPartyLastNameRequired: false,
  customerInformationPartyLastNameVisible: false,
  customerInformationPartyType: NetSheetPartyTypeEnum.Seller,
  customerInformationPartyTypes: [
    NetSheetPartyTypeEnum.Seller,
    NetSheetPartyTypeEnum.Buyer,
    NetSheetPartyTypeEnum.Borrower
  ],
  miscItems: [],
  mortgagePrimaryBalance: null,
  mortgagePrimaryBalanceVisible: false,
  mortgagePrimaryInterestRate: null,
  mortgagePrimaryInterestRateVisible: false,
  mortgagePrimaryLienCloseDate: null,
  mortgagePrimaryLienCloseDateVisible: false,
  mortgagePrimaryPrepayPenalty: null,
  mortgagePrimaryPrepayPenaltyVisible: false,
  mortgageSecondaryBalance: null,
  mortgageSecondaryBalanceVisible: false,
  mortgageSecondaryInterestRate: null,
  mortgageSecondaryInterestRateVisible: false,
  mortgageSecondaryPrepayPenalty: null,
  mortgageSecondaryPrepayPenaltyVisible: false,
  salesCommissionBrokerCommissionListing: null,
  salesCommissionBrokerCommissionListingRequired: false,
  salesCommissionBrokerCommissionListingVisible: false,
  salesCommissionBrokerCommissionSelling: null,
  salesCommissionBrokerCommissionSellingRequired: false,
  salesCommissionBrokerCommissionSellingVisible: false,
  salesCommissionBrokerCommissionSplits: false,
  salesCommissionBrokerCommissionSplitsVisible: false,
  salesCommissionBrokerCommissionTaxListing: false,
  salesCommissionBrokerCommissionTaxListingVisible: false,
  salesCommissionBrokerCommissionTaxSelling: false,
  salesCommissionBrokerCommissionTaxSellingVisible: false,
  salesCommissionBrokerCommissionTaxTotal: false,
  salesCommissionBrokerCommissionTaxTotalVisible: false,
  salesCommissionBrokerCommissionTierRate: null,
  salesCommissionBrokerCommissionTierRateVisible: false,
  salesCommissionBrokerCommissionTotal: null,
  salesCommissionBrokerCommissionTotalRequired: false,
  salesCommissionBrokerCommissionTotalVisible: false,
  taxes: null,
  taxesHomeownersDuesAmount: null,
  taxesHomeownersDuesAmountVisible: false,
  taxesHomeownersDuesDueDate: null,
  taxesHomeownersDuesDueDateVisible: false,
  taxesHomeownersDuesPaid: false,
  taxesHomeownersDuesPaidVisible: false,
  taxesHomeownersDuesTerm: NetSheetTermEnum.Monthly,
  taxesHomeownersDuesTermVisible: false,
  taxesNextInstallment: null,
  taxesNextInstallmentVisible: false,
  taxesPaid: false,
  taxesPaidToDateDropdown: null,
  taxesPaidToDateDropdownVisible: false,
  taxesPaidToDatePicker: null,
  taxesPaidToDatePickerVisible: false,
  taxesPaidVisible: false,
  taxesTerm: NetSheetTermEnum.SemiAnnually,
  taxesTermVisible: false,
  taxesVisible: false,
  transactionInformationBrokerageFee: null,
  transactionInformationBrokerageFeeVisible: false,
  transactionInformationBuyerCreditAmount: null,
  transactionInformationBuyerCreditAmountVisible: false,
  transactionInformationCityTransferTaxSplit: NetSheetSplitOptionsEnum.Seller,
  transactionInformationCityTransferTaxSplitVisible: false,
  transactionInformationClosingDate: null,
  transactionInformationClosingDateRequired: true,
  transactionInformationClosingDateVisible: true,
  transactionInformationCountyTransferTaxSplit: NetSheetSplitOptionsEnum.Seller,
  transactionInformationCountyTransferTaxSplitVisible: false,
  transactionInformationEscrowBranch: null,
  transactionInformationEscrowBranches: [],
  transactionInformationEscrowBranchRequired: false,
  transactionInformationEscrowBranchVisible: false,
  transactionInformationEscrowCities: [],
  transactionInformationEscrowCity: null,
  transactionInformationEscrowCityVisible: false,
  transactionInformationEscrowCounties: [],
  transactionInformationEscrowCounty: null,
  transactionInformationEscrowCountyRequired: true,
  transactionInformationEscrowCountyVisible: false,
  transactionInformationEscrowFeeSplit: NetSheetSplitOptionsEnum.Buyer,
  transactionInformationEscrowFeeSplitVisible: false,
  transactionInformationEscrowState: null,
  transactionInformationEscrowStateRequired: false,
  transactionInformationEscrowStates: [],
  transactionInformationEscrowStateVisible: false,
  transactionInformationFirpta: true,
  transactionInformationFirptaVisible: false,
  transactionInformationFtb: true,
  transactionInformationFtbVisible: false,
  transactionInformationHarpta: false,
  transactionInformationHarptaVisible: false,
  transactionInformationHazardInsuranceAmount: null,
  transactionInformationHazardInsuranceAmountVisible: false,
  transactionInformationLoanPrimaryAmount: null,
  transactionInformationLoanPrimaryAmountRequired: false,
  transactionInformationLoanPrimaryAmountVisible: false,
  transactionInformationLoanPrimaryInterestRate: null,
  transactionInformationLoanPrimaryInterestRateVisible: false,
  transactionInformationLoanPrimaryOriginationFees: null,
  transactionInformationLoanPrimaryOriginationFeesVisible: false,
  transactionInformationMortgagePayoffCount: null,
  transactionInformationMortgagePayoffCountVisible: false,
  transactionInformationNaturalHazardDisclosure: null,
  transactionInformationNaturalHazardDisclosureRequired: false,
  transactionInformationNaturalHazardDisclosureVisible: false,
  transactionInformationNewLoan: false,
  transactionInformationNewLoanVisible: false,
  transactionInformationNotaryFee: null,
  transactionInformationNotaryFeeVisible: false,
  transactionInformationOwnersExemption: false,
  transactionInformationOwnersExemptionVisible: false,
  transactionInformationOwnersPolicySplit: NetSheetSplitOptionsEnum.Buyer,
  transactionInformationOwnersPolicySplitVisible: false,
  transactionInformationPersonalResidence: false,
  transactionInformationPersonalResidenceVisible: false,
  transactionInformationPropertyAddress: null,
  transactionInformationPropertyAddressRequired: true,
  transactionInformationPropertyAddressVisible: true,
  transactionInformationPropertyCities: [],
  transactionInformationPropertyCity: null,
  transactionInformationPropertyCityRequired: false,
  transactionInformationPropertyCityVisible: true,
  transactionInformationPropertyCounties: [],
  transactionInformationPropertyCounty: null,
  transactionInformationPropertyCountyRequired: false,
  transactionInformationPropertyIncorporatedArea: false,
  transactionInformationPropertyIncorporatedAreaVisible: false,
  transactionInformationPropertyState: null,
  transactionInformationPropertyStateRequired: true,
  transactionInformationPropertyStates: [],
  transactionInformationPropertyZip: null,
  transactionInformationPropertyZips: [],
  transactionInformationPropertyZipVisible: true,
  transactionInformationReo: false,
  transactionInformationReoVisible: false,
  transactionInformationSalesPrice: null,
  transactionInformationSalesPriceRequired: false,
  transactionInformationSalesPriceVisible: false,
  transactionInformationSewerBill: null,
  transactionInformationSewerBillVisible: false,
  transactionInformationShortSale: false,
  transactionInformationShortSaleVisible: false,
  transactionInformationSubEscrow: false,
  transactionInformationSubEscrowVisible: false,
  transactionInformationSurveyAmendmentSplit: false,
  transactionInformationSurveyAmendmentSplitVisible: false,
  transactionInformationWarrantyPremiumAmount: null,
  transactionInformationWarrantyPremiumAmountVisible: false,
  transactionInformationWaterBill: null,
  transactionInformationWaterBillVisible: false,
  userInformationCompany: null,
  userInformationEmail: null,
  userInformationFirstName: null,
  userInformationLastName: null,
  userInformationPhone: null
};

function getMonthOfDateValue(date: string | null, format: string) {
  const luxonDate = DateTime.fromFormat(date ?? '', format);
  return luxonDate.isValid ? luxonDate.month : -1;
}

export function updateNetSheetRules(state: State): State {
  const isSeller = state.customerInformationPartyType === NetSheetPartyTypeEnum.Seller;
  const isBuyer = state.customerInformationPartyType === NetSheetPartyTypeEnum.Buyer;
  const isBorrower = state.customerInformationPartyType === NetSheetPartyTypeEnum.Borrower;
  const estimatedClosingMonth = getMonthOfDateValue(state.transactionInformationClosingDate, 'MM/dd/yyyy');
  const isORTNetSheet = state.transactionInformationEscrowStates.length > 10;
  return {
    ...state,
    customerInformationPartyFirstNameRequired: isORTNetSheet,
    customerInformationPartyFirstNameVisible: isORTNetSheet,
    customerInformationPartyFullNameRequired: !isORTNetSheet,
    customerInformationPartyFullNameVisible: !isORTNetSheet,
    customerInformationPartyLastNameRequired: isORTNetSheet,
    customerInformationPartyLastNameVisible: isORTNetSheet,
    customerInformationPartyTypes: isORTNetSheet
      ? [NetSheetPartyTypeEnum.Seller, NetSheetPartyTypeEnum.Buyer, NetSheetPartyTypeEnum.Borrower]
      : [NetSheetPartyTypeEnum.Seller],
    mortgagePrimaryBalanceVisible: isSeller || isBorrower,
    mortgagePrimaryInterestRateVisible: isSeller || isBorrower,
    mortgagePrimaryLienCloseDateVisible:
      (isSeller || isBorrower) &&
      !isNull(state.transactionInformationLoanPrimaryAmount) &&
      state.transactionInformationPropertyState === NetSheetStateEnum.TX,
    mortgagePrimaryPrepayPenaltyVisible: isSeller || isBorrower,
    mortgageSecondaryBalanceVisible: isSeller || isBorrower,
    mortgageSecondaryInterestRateVisible: isSeller || isBorrower,
    mortgageSecondaryPrepayPenaltyVisible: isSeller || isBorrower,
    salesCommissionBrokerCommissionListingRequired: isSeller && state.salesCommissionBrokerCommissionSplits,
    salesCommissionBrokerCommissionListingVisible: isSeller && state.salesCommissionBrokerCommissionSplits,
    salesCommissionBrokerCommissionSellingRequired: isSeller && state.salesCommissionBrokerCommissionSplits,
    salesCommissionBrokerCommissionSellingVisible: isSeller && state.salesCommissionBrokerCommissionSplits,
    salesCommissionBrokerCommissionSplitsVisible: isSeller,
    salesCommissionBrokerCommissionTaxListingVisible:
      isSeller &&
      state.salesCommissionBrokerCommissionSplits &&
      state.transactionInformationPropertyState === NetSheetStateEnum.HI,
    salesCommissionBrokerCommissionTaxSellingVisible:
      isSeller &&
      state.salesCommissionBrokerCommissionSplits &&
      state.transactionInformationPropertyState === NetSheetStateEnum.HI,
    salesCommissionBrokerCommissionTaxTotalVisible:
      isSeller &&
      !state.salesCommissionBrokerCommissionSplits &&
      !isNull(state.salesCommissionBrokerCommissionTotal) &&
      state.transactionInformationPropertyState === NetSheetStateEnum.HI,
    salesCommissionBrokerCommissionTierRateVisible:
      isSeller &&
      !isNull(state.salesCommissionBrokerCommissionTotal) &&
      state.transactionInformationPropertyState === NetSheetStateEnum.OH,
    salesCommissionBrokerCommissionTotalRequired: isSeller && !state.salesCommissionBrokerCommissionSplits,
    salesCommissionBrokerCommissionTotalVisible: isSeller,
    taxesHomeownersDuesAmountVisible: isBuyer || isSeller,
    taxesHomeownersDuesDueDateVisible:
      (isBuyer || isSeller) && state.transactionInformationPropertyState !== NetSheetStateEnum.TX,
    taxesHomeownersDuesPaid:
      (isBuyer || isSeller) &&
      state.transactionInformationPropertyState === NetSheetStateEnum.TX &&
      estimatedClosingMonth === 1
        ? state.taxesHomeownersDuesPaid
        : true,
    taxesHomeownersDuesPaidVisible:
      (isBuyer || isSeller) &&
      estimatedClosingMonth === 1 &&
      state.transactionInformationPropertyState === NetSheetStateEnum.TX,
    taxesHomeownersDuesTerm:
      state.transactionInformationPropertyState === NetSheetStateEnum.TX
        ? NetSheetTermEnum.Annually
        : state.taxesHomeownersDuesTerm,
    taxesHomeownersDuesTermVisible:
      (isBuyer || isSeller) && state.transactionInformationPropertyState !== NetSheetStateEnum.TX,
    taxesNextInstallmentVisible:
      (isBuyer || isSeller) &&
      !isNull(state.transactionInformationPropertyState) &&
      [NetSheetStateEnum.AZ, NetSheetStateEnum.HI, NetSheetStateEnum.NV, NetSheetStateEnum.WA].includes(
        state.transactionInformationPropertyState
      ),
    taxesPaid:
      !isNull(state.transactionInformationPropertyState) &&
      ([NetSheetStateEnum.NM, NetSheetStateEnum.OR, NetSheetStateEnum.UT].includes(
        state.transactionInformationPropertyState
      ) ||
        (state.transactionInformationPropertyState === NetSheetStateEnum.TX && estimatedClosingMonth === 12) ||
        (state.transactionInformationPropertyState === NetSheetStateEnum.OK &&
          [11, 12].includes(estimatedClosingMonth)))
        ? state.taxesPaid
        : false,
    taxesPaidToDateDropdownVisible:
      (isBuyer || isSeller) &&
      !isNull(state.transactionInformationPropertyState) &&
      [NetSheetStateEnum.CA, NetSheetStateEnum.OH].includes(state.transactionInformationPropertyState),
    taxesPaidToDatePickerVisible:
      (isBuyer || isSeller) &&
      !isNull(state.transactionInformationPropertyState) &&
      [NetSheetStateEnum.AZ, NetSheetStateEnum.HI, NetSheetStateEnum.NV, NetSheetStateEnum.WA].includes(
        state.transactionInformationPropertyState
      ),
    taxesPaidVisible:
      (isBuyer || isSeller) &&
      !isNull(state.transactionInformationPropertyState) &&
      ([NetSheetStateEnum.NM, NetSheetStateEnum.OR, NetSheetStateEnum.UT].includes(
        state.transactionInformationPropertyState
      ) ||
        (state.transactionInformationPropertyState === NetSheetStateEnum.TX && estimatedClosingMonth === 12) ||
        (state.transactionInformationPropertyState === NetSheetStateEnum.OH &&
          [11, 12].includes(estimatedClosingMonth))),
    taxesTerm:
      !isNull(state.taxes) &&
      !isNull(state.transactionInformationPropertyState) &&
      [NetSheetStateEnum.CA, NetSheetStateEnum.OH].includes(state.transactionInformationPropertyState)
        ? NetSheetTermEnum.Annually
        : state.taxesTerm,
    taxesTermVisible:
      (isBuyer || isSeller) &&
      !isNull(state.transactionInformationPropertyState) &&
      [NetSheetStateEnum.AZ, NetSheetStateEnum.HI, NetSheetStateEnum.NV, NetSheetStateEnum.WA].includes(
        state.transactionInformationPropertyState
      ),
    taxesVisible: isBuyer || isSeller,
    transactionInformationBrokerageFeeVisible:
      (isBuyer || isSeller) && state.transactionInformationPropertyState === NetSheetStateEnum.OH,
    transactionInformationBuyerCreditAmountVisible:
      isBuyer || isSeller || state.transactionInformationPropertyState === NetSheetStateEnum.TX,
    transactionInformationCityTransferTaxSplitVisible: !isORTNetSheet && (isBuyer || isSeller),
    transactionInformationCountyTransferTaxSplitVisible: !isORTNetSheet && (isBuyer || isSeller),
    transactionInformationEscrowBranchVisible: state.transactionInformationEscrowBranches.length > 0,
    transactionInformationEscrowCityVisible:
      (isNull(state.transactionInformationEscrowCounty) || state.transactionInformationEscrowCities.length > 0) &&
      state.transactionInformationEscrowState === NetSheetStateEnum.WA,
    transactionInformationEscrowCountyRequired: state.transactionInformationEscrowCounties.length > 0,
    transactionInformationEscrowCountyVisible: state.transactionInformationEscrowCounties.length > 0,
    transactionInformationEscrowFeeSplitVisible: !isORTNetSheet && (isBuyer || isSeller),
    transactionInformationEscrowStateRequired: state.transactionInformationEscrowStates.length > 0,
    transactionInformationEscrowStateVisible: state.transactionInformationEscrowStates.length > 0,
    transactionInformationFirptaVisible: isSeller,
    transactionInformationFtbVisible: isSeller && state.transactionInformationPropertyState === NetSheetStateEnum.CA,
    transactionInformationHarptaVisible: isSeller && state.transactionInformationPropertyState === NetSheetStateEnum.HI,
    transactionInformationHazardInsuranceAmountVisible: isBuyer,
    transactionInformationLoanPrimaryAmountRequired: isBuyer || isBorrower,
    transactionInformationLoanPrimaryAmountVisible: isBuyer || isBorrower,
    transactionInformationLoanPrimaryInterestRateVisible: isBuyer || isBorrower,
    transactionInformationLoanPrimaryOriginationFeesVisible: isBuyer || isBorrower,
    transactionInformationNaturalHazardDisclosureRequired:
      !isORTNetSheet && isSeller && state.transactionInformationPropertyState === NetSheetStateEnum.CA,
    transactionInformationNaturalHazardDisclosureVisible:
      !isORTNetSheet && isSeller && state.transactionInformationPropertyState === NetSheetStateEnum.CA,
    transactionInformationNewLoanVisible:
      isSeller &&
      state.transactionInformationPropertyState === NetSheetStateEnum.CA &&
      state.transactionInformationEscrowCounty === 'Stanislaus',
    transactionInformationNotaryFeeVisible: !isORTNetSheet && (isBuyer || isSeller),
    transactionInformationOwnersExemptionVisible:
      (isBuyer || isSeller) && state.transactionInformationPropertyState === NetSheetStateEnum.HI,
    transactionInformationOwnersPolicySplitVisible:
      (isBuyer || isSeller) && (state.transactionInformationPropertyState === NetSheetStateEnum.TX || !isORTNetSheet),
    transactionInformationPersonalResidenceVisible: isSeller && !state.transactionInformationFirpta,
    transactionInformationPropertyAddressRequired: !isORTNetSheet,
    transactionInformationPropertyCityRequired:
      state.transactionInformationPropertyCities.length > 0 &&
      state.transactionInformationPropertyState !== NetSheetStateEnum.OH,
    transactionInformationPropertyCityVisible:
      (isNull(state.transactionInformationPropertyCounty) || state.transactionInformationPropertyCities.length > 0) &&
      state.transactionInformationPropertyState !== NetSheetStateEnum.OH,
    transactionInformationPropertyCountyRequired: state.transactionInformationPropertyCounties.length > 0,
    transactionInformationPropertyIncorporatedAreaVisible:
      isSeller &&
      state.transactionInformationPropertyState === NetSheetStateEnum.CA &&
      state.transactionInformationPropertyCounty === 'Riverside' &&
      state.transactionInformationPropertyCity === 'Riverside',
    transactionInformationPropertyZipVisible:
      (isNull(state.transactionInformationPropertyCity) || state.transactionInformationPropertyZips.length > 0) &&
      state.transactionInformationPropertyState !== NetSheetStateEnum.OH,
    transactionInformationReoVisible: isBuyer || isSeller,
    transactionInformationSalesPriceRequired: isBuyer || isSeller,
    transactionInformationSalesPriceVisible: isBuyer || isSeller,
    transactionInformationSewerBillVisible:
      isSeller && state.transactionInformationPropertyState === NetSheetStateEnum.OH,
    transactionInformationShortSaleVisible: isBuyer || isSeller,
    transactionInformationSubEscrowVisible:
      (isBorrower || isSeller) &&
      isORTNetSheet &&
      state.transactionInformationEscrowState === NetSheetStateEnum.CA &&
      !isNull(state.transactionInformationEscrowCounty) &&
      availableSouthernCaliforniaCounties.includes(state.transactionInformationEscrowCounty),
    transactionInformationSurveyAmendmentSplitVisible:
      isSeller && state.transactionInformationPropertyState === NetSheetStateEnum.TX,
    transactionInformationWarrantyPremiumAmountVisible: isSeller,
    transactionInformationWaterBillVisible:
      isSeller && state.transactionInformationPropertyState === NetSheetStateEnum.OH
  };
}

export const reducer = createReducer(
  initialState,
  on(fromNetSheetActions.loadTransactionInformationLocationStatesSuccess, (state, { payload }) => {
    const stateOption = findOption(payload.states, state.transactionInformationPropertyState);
    const escrowStateOption = findOption(payload.states, state.transactionInformationEscrowState);

    return updateNetSheetRules(
      payload.isEscrow
        ? {
            ...state,
            ...(payload.states.length === 1
              ? {
                  transactionInformationEscrowState:
                    first(payload.states)?.value ?? initialState.transactionInformationEscrowState
                }
              : {
                  ...(isUndefined(escrowStateOption)
                    ? {
                        transactionInformationEscrowBranch: initialState.transactionInformationEscrowBranch,
                        transactionInformationEscrowBranches: initialState.transactionInformationEscrowBranches,
                        transactionInformationEscrowCities: initialState.transactionInformationEscrowCities,
                        transactionInformationEscrowCity: initialState.transactionInformationEscrowCity,
                        transactionInformationEscrowCounties: initialState.transactionInformationEscrowCounties,
                        transactionInformationEscrowCounty: initialState.transactionInformationEscrowCounty,
                        transactionInformationEscrowState: initialState.transactionInformationEscrowState
                      }
                    : {
                        transactionInformationEscrowState: escrowStateOption.value as NetSheetStateEnum
                      })
                }),
            transactionInformationEscrowStates: payload.states
          }
        : {
            ...state,
            ...(payload.states.length === 1
              ? {
                  transactionInformationPropertyState:
                    first(payload.states)?.value ?? initialState.transactionInformationPropertyState
                }
              : {
                  ...(isUndefined(stateOption)
                    ? {
                        transactionInformationPropertyCities: initialState.transactionInformationPropertyCities,
                        transactionInformationPropertyCity: initialState.transactionInformationPropertyCity,
                        transactionInformationPropertyCounties: initialState.transactionInformationPropertyCounties,
                        transactionInformationPropertyCounty: initialState.transactionInformationPropertyCounty,
                        transactionInformationPropertyState: initialState.transactionInformationPropertyState,
                        transactionInformationPropertyZip: initialState.transactionInformationPropertyZip,
                        transactionInformationPropertyZips: initialState.transactionInformationPropertyZips
                      }
                    : {
                        transactionInformationPropertyState: stateOption.value as NetSheetStateEnum
                      })
                }),
            transactionInformationPropertyStates: payload.states
          }
    );
  }),
  on(fromNetSheetActions.loadTransactionInformationLocationCountiesSuccess, (state, { payload }) => {
    const countyOption = findOption(payload.counties, state.transactionInformationPropertyCounty);
    const escrowCountyOption = findOption(payload.counties, state.transactionInformationEscrowCounty);
    return updateNetSheetRules(
      payload.isEscrow
        ? {
            ...state,
            ...(payload.counties.length === 1
              ? {
                  transactionInformationEscrowCounty:
                    first(payload.counties)?.value ?? initialState.transactionInformationEscrowCounty
                }
              : {
                  ...(isUndefined(escrowCountyOption)
                    ? {
                        transactionInformationEscrowBranch: initialState.transactionInformationEscrowBranch,
                        transactionInformationEscrowBranches: initialState.transactionInformationEscrowBranches,
                        transactionInformationEscrowCities: initialState.transactionInformationEscrowCities,
                        transactionInformationEscrowCity: initialState.transactionInformationEscrowCity,
                        transactionInformationEscrowCounty: initialState.transactionInformationEscrowCounty
                      }
                    : {
                        transactionInformationEscrowCounty: escrowCountyOption.value
                      })
                }),
            transactionInformationEscrowCounties: payload.counties
          }
        : {
            ...state,
            ...(payload.counties.length === 1
              ? {
                  transactionInformationPropertyCounty:
                    first(payload.counties)?.value ?? initialState.transactionInformationPropertyCounty
                }
              : {
                  ...(isUndefined(countyOption)
                    ? {
                        transactionInformationPropertyCities: initialState.transactionInformationPropertyCities,
                        transactionInformationPropertyCity: initialState.transactionInformationPropertyCity,
                        transactionInformationPropertyCounty: initialState.transactionInformationPropertyCounty,
                        transactionInformationPropertyZip: initialState.transactionInformationPropertyZip,
                        transactionInformationPropertyZips: initialState.transactionInformationPropertyZips
                      }
                    : {
                        transactionInformationEscrowCounty: countyOption.value
                      })
                }),
            transactionInformationPropertyCounties: payload.counties
          }
    );
  }),
  on(fromNetSheetActions.loadTransactionInformationLocationCitiesSuccess, (state, { payload }) => {
    const cityOption = findOption(payload.cities, state.transactionInformationPropertyCity);
    const escrowCityOption = findOption(payload.cities, state.transactionInformationEscrowCity);

    return updateNetSheetRules(
      payload.isEscrow
        ? {
            ...state,
            ...(payload.cities.length === 1
              ? {
                  transactionInformationEscrowCity:
                    first(payload.cities)?.value ?? initialState.transactionInformationEscrowCity
                }
              : {
                  ...(isUndefined(escrowCityOption)
                    ? {
                        transactionInformationEscrowCity: initialState.transactionInformationEscrowCity
                      }
                    : {
                        transactionInformationEscrowCity: escrowCityOption.value
                      })
                }),
            transactionInformationEscrowCities: payload.cities
          }
        : {
            ...state,
            ...(payload.cities.length === 1
              ? {
                  transactionInformationPropertyCity:
                    first(payload.cities)?.value ?? initialState.transactionInformationPropertyCity
                }
              : {
                  ...(isUndefined(cityOption)
                    ? {
                        transactionInformationPropertyCity: initialState.transactionInformationPropertyCity,
                        transactionInformationPropertyZip: initialState.transactionInformationPropertyZip,
                        transactionInformationPropertyZips: initialState.transactionInformationPropertyZips
                      }
                    : {
                        transactionInformationPropertyCity: cityOption.value
                      })
                }),
            transactionInformationPropertyCities: payload.cities
          }
    );
  }),
  on(fromNetSheetActions.loadTransactionInformationLocationZipsSuccess, (state, { payload }) => {
    const zipOption = findOption(payload.zips, state.transactionInformationPropertyZip);
    return updateNetSheetRules({
      ...state,
      ...(payload.zips.length === 1
        ? {
            transactionInformationPropertyZip:
              first(payload.zips)?.value ?? initialState.transactionInformationPropertyZip
          }
        : {
            ...(isUndefined(zipOption)
              ? {
                  transactionInformationPropertyZip: initialState.transactionInformationPropertyZip
                }
              : {
                  transactionInformationPropertyZip: zipOption.value
                })
          }),
      transactionInformationPropertyZips: payload.zips
    });
  }),
  on(fromNetSheetActions.loadTransactionInformationLocationEscrowBranchesSuccess, (state, { payload }) => {
    const branchOption = findOption(payload.branches, state.transactionInformationPropertyZip);

    return updateNetSheetRules({
      ...state,
      ...(payload.branches.length === 1
        ? {
            transactionInformationEscrowBranch:
              first(payload.branches)?.value ?? initialState.transactionInformationEscrowBranch
          }
        : {
            ...(isUndefined(branchOption)
              ? {
                  transactionInformationEscrowBranch: initialState.transactionInformationEscrowBranch
                }
              : {
                  transactionInformationEscrowBranch: branchOption.value
                })
          }),
      transactionInformationEscrowBranches: payload.branches
    });
  }),
  on(
    fromNetSheetActions.updateCustomerInformation,
    (
      state,
      { payload: { customerInformationPartyFirstName, customerInformationPartyLastName, customerInformationPartyType } }
    ) => {
      let newState = {
        ...state,
        ...(!isNil(customerInformationPartyType) && {
          customerInformationPartyType
        }),
        ...(!isUndefined(customerInformationPartyFirstName) && {
          customerInformationPartyFirstName
        }),
        ...(!isUndefined(customerInformationPartyLastName) && {
          customerInformationPartyLastName
        })
      };

      if (!isNil(customerInformationPartyType)) {
        newState = updateNetSheetRules(newState);
      }
      return newState;
    }
  ),
  on(
    fromNetSheetActions.updateTransactionInformationLocation,
    (
      state,
      {
        payload: {
          transactionInformationEscrowBranch,
          transactionInformationEscrowCity,
          transactionInformationEscrowCounty,
          transactionInformationEscrowState,
          transactionInformationPropertyCity,
          transactionInformationPropertyCounty,
          transactionInformationPropertyState,
          transactionInformationPropertyZip
        }
      }
    ) =>
      updateNetSheetRules({
        ...state,
        ...(!isUndefined(transactionInformationEscrowBranch) && {
          transactionInformationEscrowBranch
        }),
        ...(!isUndefined(transactionInformationEscrowCity) && {
          transactionInformationEscrowCity
        }),
        ...(!isUndefined(transactionInformationPropertyCity) && { transactionInformationPropertyCity }),
        ...(!isUndefined(transactionInformationPropertyZip) && { transactionInformationPropertyZip }),
        ...(!isUndefined(transactionInformationEscrowCounty) && {
          transactionInformationEscrowCounty
        }),
        ...(!isUndefined(transactionInformationEscrowState) && {
          transactionInformationEscrowState
        }),
        ...(!isUndefined(transactionInformationPropertyCounty) && { transactionInformationPropertyCounty }),
        ...(!isUndefined(transactionInformationPropertyState) && { transactionInformationPropertyState })
      })
  ),
  on(
    fromNetSheetActions.updateTransactionInformation,
    (
      state,
      {
        payload: {
          transactionInformationBrokerageFee,
          transactionInformationBuyerCreditAmount,
          transactionInformationCityTransferTaxSplit,
          transactionInformationClosingDate,
          transactionInformationCountyTransferTaxSplit,
          transactionInformationEscrowFeeSplit,
          transactionInformationFirpta,
          transactionInformationFtb,
          transactionInformationHarpta,
          transactionInformationHazardInsuranceAmount,
          transactionInformationLoanPrimaryAmount,
          transactionInformationLoanPrimaryInterestRate,
          transactionInformationLoanPrimaryOriginationFees,
          transactionInformationMortgagePayoffCount,
          transactionInformationNaturalHazardDisclosure,
          transactionInformationNewLoan,
          transactionInformationOwnersExemption,
          transactionInformationOwnersPolicySplit,
          transactionInformationPersonalResidence,
          transactionInformationPropertyAddress,
          transactionInformationPropertyIncorporatedArea,
          transactionInformationReo,
          transactionInformationSalesPrice,
          transactionInformationSewerBill,
          transactionInformationShortSale,
          transactionInformationSubEscrow,
          transactionInformationSurveyAmendmentSplit,
          transactionInformationWarrantyPremiumAmount,
          transactionInformationWaterBill
        }
      }
    ) =>
      updateNetSheetRules({
        ...state,
        ...(!isNil(transactionInformationReo) && {
          transactionInformationReo,
          ...(transactionInformationReo && {
            transactionInformationShortSale: false
          })
        }),
        ...(!isNil(transactionInformationShortSale) && {
          transactionInformationShortSale,
          ...(transactionInformationShortSale && {
            transactionInformationReo: false
          })
        }),
        ...(!isNil(transactionInformationCityTransferTaxSplit) && { transactionInformationCityTransferTaxSplit }),
        ...(!isNil(transactionInformationEscrowFeeSplit) && { transactionInformationEscrowFeeSplit }),
        ...(!isNil(transactionInformationFirpta) && { transactionInformationFirpta }),
        ...(!isUndefined(transactionInformationPropertyAddress) && { transactionInformationPropertyAddress }),
        ...(!isUndefined(transactionInformationClosingDate) && { transactionInformationClosingDate }),
        ...(!isUndefined(transactionInformationLoanPrimaryAmount) && { transactionInformationLoanPrimaryAmount }),
        ...(!isUndefined(transactionInformationLoanPrimaryOriginationFees) && {
          transactionInformationLoanPrimaryOriginationFees
        }),
        ...(!isUndefined(transactionInformationMortgagePayoffCount) && { transactionInformationMortgagePayoffCount }),
        ...(!isUndefined(transactionInformationLoanPrimaryInterestRate) && {
          transactionInformationLoanPrimaryInterestRate
        }),
        ...(!isUndefined(transactionInformationNaturalHazardDisclosure) && {
          transactionInformationNaturalHazardDisclosure
        }),
        ...(!isNil(transactionInformationFtb) && { transactionInformationFtb }),
        ...(!isNil(transactionInformationHarpta) && { transactionInformationHarpta }),
        ...(!isNil(transactionInformationNewLoan) && { transactionInformationNewLoan }),
        ...(!isNil(transactionInformationOwnersExemption) && { transactionInformationOwnersExemption }),
        ...(!isNil(transactionInformationOwnersPolicySplit) && { transactionInformationOwnersPolicySplit }),
        ...(!isNil(transactionInformationPersonalResidence) && { transactionInformationPersonalResidence }),
        ...(!isNil(transactionInformationSubEscrow) && { transactionInformationSubEscrow }),
        ...(!isNil(transactionInformationSurveyAmendmentSplit) && { transactionInformationSurveyAmendmentSplit }),
        ...(!isNil(transactionInformationPropertyIncorporatedArea) && {
          transactionInformationPropertyIncorporatedArea
        }),
        ...(!isUndefined(transactionInformationBrokerageFee) && { transactionInformationBrokerageFee }),
        ...(!isUndefined(transactionInformationSalesPrice) && { transactionInformationSalesPrice }),
        ...(!isNil(transactionInformationCountyTransferTaxSplit) && { transactionInformationCountyTransferTaxSplit }),
        ...(!isUndefined(transactionInformationSewerBill) && { transactionInformationSewerBill }),
        ...(!isUndefined(transactionInformationWaterBill) && { transactionInformationWaterBill }),
        ...(!isUndefined(transactionInformationBuyerCreditAmount) && { transactionInformationBuyerCreditAmount }),
        ...(!isUndefined(transactionInformationHazardInsuranceAmount) && {
          transactionInformationHazardInsuranceAmount
        }),
        ...(!isUndefined(transactionInformationWarrantyPremiumAmount) && {
          transactionInformationWarrantyPremiumAmount
        })
      })
  ),
  on(
    fromNetSheetActions.updateMortgage,
    (
      state,
      {
        payload: {
          mortgagePrimaryBalance,
          mortgagePrimaryInterestRate,
          mortgagePrimaryLienCloseDate,
          mortgagePrimaryPrepayPenalty,
          mortgageSecondaryBalance,
          mortgageSecondaryInterestRate,
          mortgageSecondaryPrepayPenalty
        }
      }
    ) => ({
      ...state,
      ...(!isUndefined(mortgagePrimaryBalance) && { mortgagePrimaryBalance }),
      ...(!isUndefined(mortgageSecondaryBalance) && { mortgageSecondaryBalance }),
      ...(!isUndefined(mortgagePrimaryInterestRate) && { mortgagePrimaryInterestRate }),
      ...(!isUndefined(mortgageSecondaryInterestRate) && { mortgageSecondaryInterestRate }),
      ...(!isUndefined(mortgagePrimaryLienCloseDate) && { mortgagePrimaryLienCloseDate }),
      ...(!isUndefined(mortgagePrimaryPrepayPenalty) && { mortgagePrimaryPrepayPenalty }),
      ...(!isUndefined(mortgageSecondaryPrepayPenalty) && { mortgageSecondaryPrepayPenalty })
    })
  ),
  on(
    fromNetSheetActions.updateSalesCommissions,
    (
      state,
      {
        payload: {
          salesCommissionBrokerCommissionListing,
          salesCommissionBrokerCommissionSelling,
          salesCommissionBrokerCommissionSplits,
          salesCommissionBrokerCommissionTaxListing,
          salesCommissionBrokerCommissionTaxSelling,
          salesCommissionBrokerCommissionTaxTotal,
          salesCommissionBrokerCommissionTierRate,
          salesCommissionBrokerCommissionTotal
        }
      }
    ) =>
      updateNetSheetRules({
        ...state,
        ...(!isUndefined(salesCommissionBrokerCommissionTotal) && { salesCommissionBrokerCommissionTotal }),
        ...(!isUndefined(salesCommissionBrokerCommissionTierRate) && { salesCommissionBrokerCommissionTierRate }),
        ...(!isUndefined(salesCommissionBrokerCommissionTaxTotal) && { salesCommissionBrokerCommissionTaxTotal }),
        ...(!isUndefined(salesCommissionBrokerCommissionTaxSelling) && { salesCommissionBrokerCommissionTaxSelling }),
        ...(!isUndefined(salesCommissionBrokerCommissionTaxListing) && { salesCommissionBrokerCommissionTaxListing }),
        ...(!isUndefined(salesCommissionBrokerCommissionSelling) && { salesCommissionBrokerCommissionSelling }),
        ...(!isUndefined(salesCommissionBrokerCommissionListing) && { salesCommissionBrokerCommissionListing }),
        ...(!isUndefined(salesCommissionBrokerCommissionSplits) && { salesCommissionBrokerCommissionSplits })
      })
  ),
  on(
    fromNetSheetActions.updateTaxes,
    (
      state,
      {
        payload: {
          taxes,
          taxesHomeownersDuesAmount,
          taxesHomeownersDuesDueDate,
          taxesHomeownersDuesPaid,
          taxesHomeownersDuesTerm,
          taxesNextInstallment,
          taxesPaid,
          taxesPaidToDateDropdown,
          taxesPaidToDatePicker,
          taxesTerm
        }
      }
    ) =>
      updateNetSheetRules({
        ...state,
        ...(!isUndefined(taxesHomeownersDuesDueDate) && { taxesHomeownersDuesDueDate }),
        ...(!isUndefined(taxesNextInstallment) && { taxesNextInstallment }),
        ...(!isUndefined(taxesPaidToDateDropdown) && { taxesPaidToDateDropdown }),
        ...(!isUndefined(taxesPaidToDatePicker) && { taxesPaidToDatePicker }),
        ...(!isUndefined(taxesTerm) && { taxesTerm }),
        ...(!isUndefined(taxesPaid) && { taxesPaid }),
        ...(!isUndefined(taxesHomeownersDuesPaid) && { taxesHomeownersDuesPaid }),
        ...(!isUndefined(taxesHomeownersDuesTerm) && { taxesHomeownersDuesTerm }),
        ...(!isUndefined(taxesHomeownersDuesAmount) && { taxesHomeownersDuesAmount }),
        ...(!isUndefined(taxes) && { taxes })
      })
  ),
  on(fromNetSheetActions.updateMiscItems, (state, { payload: { miscItems } }) => ({
    ...state,
    miscItems
  })),
  on(
    fromNetSheetActions.updateUserInformation,
    (
      state,
      {
        payload: {
          userInformationCompany,
          userInformationEmail,
          userInformationFirstName,
          userInformationLastName,
          userInformationPhone
        }
      }
    ) => ({
      ...state,
      ...(!isUndefined(userInformationCompany) && {
        userInformationCompany
      }),
      ...(!isUndefined(userInformationEmail) && {
        userInformationEmail
      }),
      ...(!isUndefined(userInformationPhone) && {
        userInformationPhone
      }),
      ...(!isUndefined(userInformationLastName) && {
        userInformationLastName
      }),
      ...(!isUndefined(userInformationFirstName) && {
        userInformationFirstName
      })
    })
  ),
  on(fromNetSheetActions.prefillInformation, (state, { payload }) =>
    updateNetSheetRules({
      ...state,
      customerInformationPartyType: NetSheetPartyTypeEnum.Seller,
      ...(!isUndefined(payload.firstName) && {
        customerInformationPartyFirstName: payload.firstName
      }),
      ...(!isUndefined(payload.fullName) && {
        customerInformationPartyFullName: payload.fullName
      }),
      ...(!isUndefined(payload.lastName) && {
        customerInformationPartyLastName: payload.lastName
      }),
      ...(!isUndefined(payload.address) && {
        transactionInformationPropertyAddress: payload.address
      }),
      ...(!isUndefined(payload.state) && {
        transactionInformationEscrowState: payload.state,
        transactionInformationPropertyState: payload.state
      }),
      ...(!isUndefined(payload.county) && {
        transactionInformationEscrowCounty: payload.county,
        transactionInformationPropertyCounty: payload.county
      }),
      ...(!isUndefined(payload.city) && {
        transactionInformationEscrowCity: payload.city,
        transactionInformationPropertyCity: payload.city
      }),
      ...(!isUndefined(payload.zip) && {
        transactionInformationPropertyZip: payload.zip
      }),
      ...(!isUndefined(payload.propertyTaxes) && {
        taxes: payload.propertyTaxes
      })
    })
  ),
  on(fromNetSheetActions.calculate, (state) => ({
    ...state,
    calculatorResult: null
  })),
  on(fromNetSheetActions.calculateSuccess, (state, { payload }) => ({
    ...state,
    calculatorResult: payload.calculatorReport
  })),
  on(fromNetSheetActions.resetFields, fromNetSheetActions.loadPropertyProfilePrefillInformation, (state) =>
    updateNetSheetRules({
      ...state,
      ...omit(initialState, [
        'userInformationCompany',
        'userInformationEmail',
        'userInformationPhone',
        'userInformationLastName',
        'userInformationFirstName'
      ])
    })
  )
);
